"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useWorkspace } from "@/lib/context/WorkspaceContext";
import { type UserContext, shouldDisplayRole } from "@/lib/permissions";
import { StaffSidebar, type StaffSection } from "@/components/staff/StaffSidebar";
import { OverviewPanel } from "@/components/staff/panels/OverviewPanel";
import { AssignmentsPanel } from "@/components/staff/panels/AssignmentsPanel";
import { ActivitiesPanel } from "@/components/staff/panels/ActivitiesPanel";
import { EventsPanel } from "@/components/staff/panels/EventsPanel";
import { VolunteersPanel } from "@/components/staff/panels/VolunteersPanel";
import { AnalyticsPanel } from "@/components/staff/panels/AnalyticsPanel";
import { AdmissionsReviewConsolePanel } from "@/components/staff/panels/AdmissionsReviewConsolePanel";
import { AdmissionsPanel } from "@/components/staff/panels/AdmissionsPanel";
import { GovernanceProjectsPanel } from "@/components/staff/panels/GovernanceProjectsPanel";
import { StaffDirectoryPanel } from "@/components/staff/panels/StaffDirectoryPanel";
import { PlatformInsightsPanel } from "@/components/staff/panels/PlatformInsightsPanel";
import { DirectoryContent } from "@/app/directory/page";
import { EmailAutoPanel } from "@/components/staff/panels/EmailAutoPanel";
import { FeedbackPanel } from "@/components/staff/panels/FeedbackPanel";
import { DiscordPanel } from "@/components/staff/panels/DiscordPanel";
import { PolicyConsolePanel } from "@/components/policies/console/PolicyConsolePanel";
import { PromptConsolePanel } from "@/components/prompts/console/PromptConsolePanel";
import { OrgGroupsPanel } from "@/components/dashboard/panels/OrgGroupsPanel";
import { VolunteersDirectory } from "@/app/volunteers/page";
import { CycleOversightPanel } from "@/components/candidate-hub/CycleOversightPanel";
import { GeoSettingsPanel } from "../../components/staff/panels/GeoSettingsPanel";
import { WinbackPanel } from "@/components/staff/panels/winback/WinbackPanel";
import { CandidateCampaignPanel } from "@/components/staff/panels/candidates/CandidateCampaignPanel";
import { VolunteersInterestedPanel } from "@/components/staff/panels/VolunteersInterestedPanel";
import { NinetyOpsPanel } from "@/components/staff/panels/ninety/NinetyOpsPanel";
// ─── Constants ────────────────────────────────────────────────────────────────

const STAFF_ROLES = ["staff", "admin", "superadmin"];

const SECTION_TITLES: Record<StaffSection, string> = {
    overview:    "Home",
    investment_challenge: "Investment Challenge",
    assignments: "Group Assignments",
    staff_directory: "Staff Directory",
    activities:  "All Gigs",
    events:      "Events Pipeline",
    volunteers:  "Volunteers",
    volunteers_interested: "Volunteers Interested",
    analytics:   "Analytics",
    admissions:  "Admissions",
    admissions_review: "Admissions Review",
    platform_insights: "Platform Insights",
    directory:   "Volunteer Directory",
    feedback:    "User Feedback",
    discord:     "Discord",
    email_auto:  "Email Automation",
    policy_console: "Policy Console",
    prompts_console: "Prompts Console",
    org_groups:  "Groups",
    org_volunteers: "Volunteer Snapshot",
    candidate_hub: "Exam Cycle Oversight",
    geo_settings: "Geo Pipeline Settings",
    winback:     "Member Win-back",
    candidate_campaign: "Candidate Outreach",
    governance_projects: "Governance Projects",
    ninety_ops:  "C&ME Data Points",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isStaffUser(user: UserContext | null): boolean {
    return !!user && STAFF_ROLES.includes((user.role || "").toLowerCase());
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function LoadingScreen({ message = "Loading portal…" }: { message?: string }) {
    return (
        <div className="fixed inset-0 bg-[#003d45] flex items-center justify-center z-50">
            <div className="flex flex-col items-center gap-4">
                <div className="w-10 h-10 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                <p className="text-white/50 text-[10px] font-black uppercase tracking-widest">{message}</p>
            </div>
        </div>
    );
}

function PanelRenderer({ section }: { section: StaffSection }) {
    const { user } = useWorkspace();
    switch (section) {
        case "overview":    return <OverviewPanel />;
        // "investment_challenge" is not a panel — it is its own module at
        // /staff/cmtic/*. Selecting it navigates there (see handleSelectSection).
        case "assignments": return <AssignmentsPanel />;
        case "staff_directory": return <StaffDirectoryPanel />;
        case "activities":  return <ActivitiesPanel />;
        case "events":      return <EventsPanel />;
        case "volunteers":  return <VolunteersPanel />;
        case "volunteers_interested": return <VolunteersInterestedPanel />;
        case "analytics":   return <AnalyticsPanel />;
        case "admissions":  return <AdmissionsPanel />;
        case "admissions_review": return <AdmissionsReviewConsolePanel />;
        case "platform_insights": return <PlatformInsightsPanel />;
        case "directory":   return <DirectoryContent isEmbedded={true} />;
        case "feedback":    return <FeedbackPanel />;
        case "discord":     return <DiscordPanel />;
        case "email_auto":  return <EmailAutoPanel />;
        case "policy_console": return <PolicyConsolePanel />;
        case "prompts_console": return <PromptConsolePanel />;
        case "org_groups":  return <OrgGroupsPanel />;
        case "org_volunteers": return <VolunteersDirectory embedded={true} />;
        case "candidate_hub": return <CycleOversightPanel userEmail={user?.email || ""} />;
        case "geo_settings":  return <GeoSettingsPanel />;
        case "winback":     return <WinbackPanel />;
        case "candidate_campaign": return <CandidateCampaignPanel />;
        case "governance_projects": return <GovernanceProjectsPanel />;
        case "ninety_ops":  return <NinetyOpsPanel />;
        default:            return <OverviewPanel />;
    }
}

/** Clickable profile pill → dropdown with user details + actions */
export function ProfileDropdown({ user, onLogout }: { user: UserContext; onLogout: () => void }) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    // Close on outside click
    useEffect(() => {
        function handleClick(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        }
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    const initials = `${user.first_name?.[0] ?? ""}${user.last_name?.[0] ?? ""}`.toUpperCase();

    return (
        <div ref={ref} className="relative">
            {/* Trigger */}
            <button
                onClick={() => setOpen(o => !o)}
                className="flex items-center gap-2.5 px-3 py-2 rounded-xl hover:bg-[#f0f2f4] transition-colors group"
            >
                {user.profile_photo_url ? (
                    <img src={user.profile_photo_url} alt="avatar"
                        className="w-8 h-8 rounded-full object-cover border-2 border-[#e2e8f0] group-hover:border-[#007a8a] transition-colors" />
                ) : (
                    <div className="w-8 h-8 rounded-full bg-[#003d45] text-white flex items-center justify-center text-xs font-black border-2 border-transparent group-hover:border-[#007a8a] transition-colors">
                        {initials}
                    </div>
                )}
                <div className="text-left hidden sm:block">
                    <p className="text-sm font-bold text-[#1a1a1a] leading-tight">{user.first_name} {user.last_name}</p>
                    {shouldDisplayRole(user.role) && (
                        <p className="text-[10px] font-black uppercase tracking-widest text-[#007a8a]">{user.role}</p>
                    )}
                </div>
                <span className={`material-symbols-outlined text-[#999] text-base transition-transform ${open ? "rotate-180" : ""}`}>
                    expand_more
                </span>
            </button>

            {/* Dropdown */}
            {open && (
                <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-2xl border border-[#e2e8f0] shadow-xl z-50 overflow-hidden">
                    {/* Header */}
                    <div className="bg-[#003d45] px-5 py-4 flex items-center gap-3">
                        {user.profile_photo_url ? (
                            <img src={user.profile_photo_url} alt="avatar"
                                className="w-12 h-12 rounded-full object-cover border-2 border-white/30 flex-shrink-0" />
                        ) : (
                            <div className="w-12 h-12 rounded-full bg-white/15 border-2 border-white/30 flex items-center justify-center text-white font-black text-base flex-shrink-0">
                                {initials}
                            </div>
                        )}
                        <div className="min-w-0">
                            <p className="font-black text-white text-base leading-tight truncate">{user.first_name} {user.last_name}</p>
                            <p className="text-xs text-white/60 truncate mt-0.5">{user.email}</p>
                            {shouldDisplayRole(user.role) && (
                                <span className="inline-block mt-1.5 text-[9px] font-black uppercase tracking-widest bg-white/15 text-white/80 px-2 py-0.5 rounded-full">
                                    {user.role}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Personal details */}
                    <div className="px-5 py-3 border-b border-[#f0f0f0] space-y-1.5">
                        {user.phone && (
                            <div className="flex items-center gap-2 text-sm text-[#555]">
                                <span className="material-symbols-outlined text-[#007a8a] text-[18px]">phone</span>
                                {user.phone}
                            </div>
                        )}
                        {user.member_since && (
                            <div className="flex items-center gap-2 text-sm text-[#555]">
                                <span className="material-symbols-outlined text-[#007a8a] text-[18px]">calendar_month</span>
                                Member since {user.member_since}
                            </div>
                        )}
                        {user.member_status && (
                            <div className="flex items-center gap-2 text-sm text-[#555]">
                                <span className="material-symbols-outlined text-[#007a8a] text-[18px]">verified</span>
                                <span className="capitalize">{user.member_status} member</span>
                            </div>
                        )}
                        {!user.phone && !user.member_since && !user.member_status && (
                            <p className="text-xs text-[#aaa] italic">No additional details on file.</p>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="p-2 space-y-0.5">
                        <Link href="/account" onClick={() => setOpen(false)}
                            className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#f0f2f4] transition-colors group">
                            <span className="material-symbols-outlined text-[#666] text-[18px] group-hover:text-[#003d45]">manage_accounts</span>
                            <span className="text-sm font-semibold text-[#333] group-hover:text-[#003d45]">Edit Profile</span>
                        </Link>
                        <div className="h-px bg-[#f0f0f0] mx-2 my-1" />
                        <button
                            onClick={() => { setOpen(false); onLogout(); }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-red-50 transition-colors group text-left"
                        >
                            <span className="material-symbols-outlined text-[#666] text-[18px] group-hover:text-[#ae232d]">logout</span>
                            <span className="text-sm font-semibold text-[#333] group-hover:text-[#ae232d]">Sign Out</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function StaffPortalPage() {
    const { user, isLoading, logout } = useWorkspace();
    const router = useRouter();
    const [activeSection, setActiveSection] = useState<StaffSection>("overview");
    const [mobileOpen, setMobileOpen] = useState(false);

    // Synchronize section state with ?section= query parameter
    const handleSelectSection = (s: StaffSection) => {
        // The Investment Challenge is a full module with its own shell + rail,
        // not an inline panel — hand off to it instead of rendering a second copy.
        if (s === "investment_challenge") { router.push("/staff/cmtic"); return; }
        setActiveSection(s);
        // A new section starts clean: panel-owned params (e.g. Platform
        // Insights' range/person/view) belong to the section that set them.
        const url = new URL(window.location.href);
        if (url.searchParams.get("section") !== s) url.search = "";
        url.searchParams.set("section", s);
        window.history.pushState({}, "", url.toString());
    };

    // Initialize section from URL on mount
    useEffect(() => {
        if (typeof window !== "undefined") {
            const params = new URLSearchParams(window.location.search);
            const secParam = params.get("section") as StaffSection | null;
            // Legacy deep links to the inline IC panel now land in the module.
            if (secParam === "investment_challenge") { router.replace("/staff/cmtic"); return; }
            const validSections: StaffSection[] = [
                "overview", "assignments", "staff_directory", "activities", "events",
                "volunteers", "analytics", "admissions", "admissions_review", "platform_insights", "directory", "feedback", "email_auto",
                "policy_console", "prompts_console", "org_groups", "org_volunteers", "candidate_hub", "geo_settings", "discord", "winback", "candidate_campaign", "volunteers_interested", "governance_projects", "ninety_ops"
            ];
            if (secParam && validSections.includes(secParam)) {
                setActiveSection(secParam);
            }
        }
    }, [router]);

    // Handle browser back/forward
    useEffect(() => {
        const onPop = () => {
            const s = new URLSearchParams(window.location.search).get("section") as StaffSection | null;
            if (s === "investment_challenge") { router.replace("/staff/cmtic"); return; }
            if (s) setActiveSection(s);
        };
        window.addEventListener("popstate", onPop);
        return () => window.removeEventListener("popstate", onPop);
    }, [router]);

    useEffect(() => {
        if (isLoading) return;
        if (!user) { router.replace("/login?redirectTo=/staff"); return; }
        if (!isStaffUser(user)) { router.replace("/dashboard"); }
    }, [isLoading, user, router]);

    // Restrict email_auto section and geo_settings section
    useEffect(() => {
        if (isLoading || !user) return;
        const email = (user.email || "").toLowerCase();
        const role = (user.role || "").toLowerCase();

        if (activeSection === "email_auto") {
            const allowedEmails = ["shanaia.alvares@cmtassociation.org", "joel@cmtassociation.org"];
            const isAllowed = user.role === "superadmin" || allowedEmails.includes(email);
            if (!isAllowed) {
                setActiveSection("overview");
            }
        } else if (activeSection === "geo_settings") {
            const isAllowed = role === "admin" || role === "superadmin" || role === "staff" || user.is_staff === true;
            if (!isAllowed) {
                setActiveSection("overview");
            }
        }
    }, [activeSection, user, isLoading]);

    if (isLoading) return <LoadingScreen />;
    if (!user || !isStaffUser(user)) return <LoadingScreen message="Redirecting…" />;

    return (
        <div className="fixed inset-0 flex bg-[#f0f2f4] z-40">
            {/* Sidebar */}
            <StaffSidebar
                active={activeSection}
                onSelect={handleSelectSection}
                user={user}
                onLogout={logout}
                mobileOpen={mobileOpen}
                onMobileClose={() => setMobileOpen(false)}
            />

            {/* Main content area */}
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                {/* Top navbar */}
                <header className="flex-shrink-0 bg-white border-b border-[#e2e8f0] shadow-sm">
                    {/* Navigation bar */}
                    <div className="flex items-center justify-between px-4 lg:px-6 h-14 gap-3">
                        <button
                            onClick={() => setMobileOpen(true)}
                            className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-[#f0f2f4] transition-colors flex-shrink-0"
                            aria-label="Open menu"
                        >
                            <span className="material-symbols-outlined text-[#003d45]">menu</span>
                        </button>
                        <h1 className="text-sm lg:text-base font-black text-[#003d45] uppercase tracking-wider truncate flex-1 lg:flex-none">
                            {SECTION_TITLES[activeSection]}
                        </h1>
                        <nav className="hidden sm:flex items-center gap-1">
                            <button
                                onClick={() => handleSelectSection("org_volunteers")}
                                className="px-4 py-2 text-sm font-semibold text-[#666] hover:text-[#003d45] rounded-lg hover:bg-[#f0f2f4] transition-colors"
                            >
                                Volunteer Snapshot
                            </button>
                            <button
                                onClick={() => handleSelectSection("org_groups")}
                                className="px-4 py-2 text-sm font-semibold text-[#666] hover:text-[#003d45] rounded-lg hover:bg-[#f0f2f4] transition-colors"
                            >
                                Groups
                            </button>
                        </nav>
                        <ProfileDropdown user={user} onLogout={logout} />
                    </div>
                </header>

                {/* Scrollable panel area — events panel manages its own layout */}
                <main className={`flex-1 min-h-0 ${activeSection === "events" || activeSection === "email_auto" ? "overflow-hidden p-0 flex flex-col" : "overflow-y-auto p-4 sm:p-6 lg:p-8"}`}>
                    <PanelRenderer section={activeSection} />
                </main>
            </div>
        </div>
    );
}
